import { useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'

import { ClientNavigationState } from '../../context/clientNavigation/ClientNavigation.types'
import { useClientNavigationState } from '../../context/clientNavigation/hooks'
import createIsolatedHook from '../../helpers/createIsolatedHook'
import safelyJoinSlugs from '../../helpers/safelyJoinSlugs'
import useCurrentPageNumber from '../useCurrentPageNumber'
import useGemius from '../useGemius'
import useGTM from '../useGTM'
import useUpScore from '../useUpScore'

const useMiscUpScore = ({ id = '', appRoot, slugs, pageUrl: pageUrlIn, objectType }) => {
    const pageNumber = useCurrentPageNumber()
    const upScoreParams = useMemo(() => {
        const pageUrl = pageNumber ? `${appRoot}/${safelyJoinSlugs(slugs)}?stranica=${pageNumber}` : ''
        return {
            id,
            objectType: objectType || 'misc',
            category: slugs?.[0],
            subCategory: slugs?.[1],
            nextPageUrl: pageUrlIn || pageUrl
        }
    }, [id, slugs, pageNumber, slugs, pageUrlIn, objectType, appRoot])

    useUpScore(upScoreParams)
}

const useMiscGemius = ({ gemiusId, gemiusDiff }) => {
    const listingPage = useCurrentPageNumber()
    useGemius(gemiusId, gemiusDiff || listingPage)
}

const useMiscGtm = ({ gtmDataVirtualInfo, gtmDataGeneralInfo }) => {
    const { sendDataToGTM } = useGTM()
    const navigationState = useClientNavigationState()

    useEffect(() => {
        if (!gtmDataGeneralInfo || navigationState !== ClientNavigationState.READY) {
            return
        }
        sendDataToGTM({
            event: 'pageView',
            userType: 'not logged in',
            pageType: 'static pages',
            contentID: '',
            contentTitle: 'misc',
            contentAuthor: '',
            contentCreator: '',
            ...gtmDataGeneralInfo
        })
    }, [gtmDataGeneralInfo, sendDataToGTM, navigationState])

    useEffect(() => {
        if (!gtmDataVirtualInfo || navigationState !== ClientNavigationState.READY) {
            return
        }
        sendDataToGTM({
            event: 'VirtualPageView',
            virtualPageURL: ``,
            virtualPageTitle: '',
            ...gtmDataVirtualInfo
        })
    }, [gtmDataVirtualInfo, sendDataToGTM, navigationState])
}

const IsolatedUseMiscUpScore = createIsolatedHook(useMiscUpScore)
const IsolatedUseMiscGemius = createIsolatedHook(useMiscGemius)
const IsolatedUseMiscGtm = createIsolatedHook(useMiscGtm)

export function IsolatedMiscThirdParties({
    id,
    gemiusId,
    gemiusDiff,
    appRoot,
    pageUrl,
    upscoreObjectType,
    slugs,
    gtmDataGeneralInfo,
    gtmDataVirtualInfo
}) {
    return (
        <>
            {gemiusId && <IsolatedUseMiscGemius gemiusId={gemiusId} gemiusDiff={gemiusDiff} />}
            <IsolatedUseMiscUpScore
                id={id}
                appRoot={appRoot}
                slugs={slugs}
                pageUrl={pageUrl}
                objectType={upscoreObjectType}
            />
            {(gtmDataGeneralInfo || gtmDataVirtualInfo) && (
                <IsolatedUseMiscGtm gtmDataGeneralInfo={gtmDataGeneralInfo} gtmDataVirtualInfo={gtmDataVirtualInfo} />
            )}
        </>
    )
}

IsolatedMiscThirdParties.propTypes = {
    id: PropTypes.string,
    gemiusId: PropTypes.string,
    gemiusDiff: PropTypes.string,
    appRoot: PropTypes.string.isRequired,
    pageUrl: PropTypes.string,
    upscoreObjectType: PropTypes.string,
    slugs: PropTypes.arrayOf(PropTypes.string),
    gtmDataGeneralInfo: PropTypes.shape({}),
    gtmDataVirtualInfo: PropTypes.shape({})
}

IsolatedMiscThirdParties.defaultProps = {
    id: '',
    gemiusId: undefined,
    gemiusDiff: undefined,
    slugs: [],
    pageUrl: undefined,
    upscoreObjectType: undefined,
    gtmDataGeneralInfo: undefined,
    gtmDataVirtualInfo: undefined
}
